
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Upload, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface AvatarUploadProps {
  fullName: string;
  avatarUrl: string | null;
  onAvatarChange: (url: string | null) => void;
}

export function AvatarUpload({ fullName, avatarUrl, onAvatarChange }: AvatarUploadProps) {
  const { toast } = useToast();

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const file = e.target.files[0];
    
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "File too large",
        description: "Image must be less than 5MB",
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please upload an image file",
      });
      return;
    }
    
    const fileExt = file.name.split(".").pop();
    const fileName = `client-${crypto.randomUUID()}.${fileExt}`;

    try {
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, file);

      if (uploadError) {
        console.error("Error uploading avatar:", uploadError);
        toast({
          variant: "destructive",
          title: "Upload failed",
          description: uploadError.message,
        });
        return;
      }

      const { data } = supabase.storage
        .from("avatars")
        .getPublicUrl(fileName);

      if (data) {
        onAvatarChange(data.publicUrl);
      }
    } catch (error) {
      console.error("Error handling avatar:", error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: "There was a problem uploading your image",
      });
    }
  };

  const resetAvatar = () => {
    onAvatarChange(null);
  };

  const initials = fullName 
    ? fullName.split(" ").map(n => n[0]).join("") 
    : "CL";

  return (
    <div className="flex flex-col items-center justify-center mb-6">
      <Avatar className="h-24 w-24 mb-4">
        <AvatarImage src={avatarUrl || ""} alt="Client avatar" />
        <AvatarFallback className="bg-white/10 text-white text-2xl">
          {initials}
        </AvatarFallback>
      </Avatar>
      
      <div className="flex gap-2">
        <div className="relative">
          <input
            type="file"
            id="avatar"
            accept="image/*"
            className="sr-only"
            onChange={handleImageUpload}
          />
          <Label
            htmlFor="avatar"
            className="bg-white/10 hover:bg-white/20 transition-colors flex items-center gap-2 text-white text-sm px-3 py-2 rounded-md cursor-pointer"
          >
            <Upload className="h-4 w-4" />
            Upload Photo
          </Label>
        </div>
        
        {avatarUrl && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={resetAvatar}
            className="text-white bg-black/20 border-white/30"
          >
            <X className="h-4 w-4 mr-1" />
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}
